import { useState } from 'react';
import { api } from '@/lib/api';

const ISSUE_TYPES = ['課程問題', '導師預約', '退款申請', '帳號問題', '付款異常', '其他'];

const StudentForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    order: '',
    issueType: '',
    message: '',
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.issueType || !formData.message) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await api.submitWebsiteContact({
        formType: 3,
        contactName: formData.name,
        email: formData.email,
        orderNo: formData.order,
        issueType: formData.issueType,
        message: formData.message
      });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : '提交失敗，請稍後重試');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSubmitted(false);
    setFormData({ name: '', email: '', order: '', issueType: '', message: '' });
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-5">
          <i className="ri-check-line text-emerald-600 text-2xl" />
        </div>
        <h3 className="text-xl font-bold text-[#0A1628] mb-2">已收到你的問題！</h3>
        <p className="text-slate-500 text-sm leading-relaxed max-w-xs">
          我們會在 <strong>1 個工作日</strong>內回覆到你填寫的信箱，請留意查收。
        </p>
        <div className="mt-5 flex items-center gap-2 text-xs text-slate-400">
          <i className="ri-mail-check-line text-emerald-400" />
          回覆將傳送至 {formData.email}
        </div>
        <button
          onClick={reset}
          className="mt-5 px-5 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-violet-700 transition-colors whitespace-nowrap"
        >
          再提交一個問題
        </button>
      </div>
    );
  }

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            姓名 <span className="text-rose-500">*</span>
          </label>
          <input
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="你的稱呼"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            聯絡信箱 <span className="text-rose-500">*</span>
          </label>
          <input
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="your@email.com"
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
          訂單號 <span className="text-slate-400 font-normal">（如有）</span>
        </label>
        <input
          name="order"
          type="text"
          value={formData.order}
          onChange={(e) => handleChange('order', e.target.value)}
          placeholder="例：ORD-20260324-XXXXX"
          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-2">
          問題類型 <span className="text-rose-500">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {ISSUE_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleChange('issueType', type)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                formData.issueType === type
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-violet-50 hover:text-violet-700'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
        <input type="hidden" name="issueType" value={formData.issueType} />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
          問題描述 <span className="text-rose-500">*</span>
        </label>
        <textarea
          name="message"
          required
          value={formData.message}
          onChange={(e) => {
            if (e.target.value.length <= 500) handleChange('message', e.target.value);
          }}
          placeholder="請描述你遇到的問題，越詳細越好，方便我們快速處理..."
          rows={4}
          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-violet-400 focus:bg-white transition-all resize-none"
        />
        <p className="text-xs text-slate-400 mt-1 text-right">{formData.message.length}/500</p>
      </div>

      <button
        type="submit"
        disabled={submitting || !formData.name || !formData.email || !formData.issueType || !formData.message}
        className="w-full py-3.5 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap disabled:cursor-not-allowed"
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin" />
            提交中...
          </span>
        ) : '提交問題'}
      </button>
      {submitError ? <p className="text-center text-xs text-rose-500">{submitError}</p> : null}
    </form>
  );
};

export default StudentForm;
